import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from 'components/ui/input-group';
import { Control, Controller, FieldPath, FieldValues, Validate } from 'react-hook-form';

const HTTPS = 'https://';
const SCHEME = /^\s*https?:\/\//i;

interface Props<T extends FieldValues> {
    control: Control<T>;
    name: FieldPath<T>;
    label: string;
    validate?: Validate<string, T>;
}

/**
 * A link field with `https://` fixed in front of it. The form holds the whole address, prefix included, so what is
 * saved is the same as when the prefix was typed; a pasted address has its own scheme dropped for the fixed one.
 */
export function HttpsLinkInput<T extends FieldValues>({ control, name, label, validate }: Props<T>) {
    return (
        <Controller
            control={control}
            name={name}
            rules={validate ? { validate: (value, values) => validate(String(value ?? ''), values) } : undefined}
            render={({ field: { ref, value, onChange, onBlur }, fieldState: { error } }) => {
                const rest = String(value ?? '').replace(SCHEME, '');
                return (
                    <InputWrapper
                        name={`field_${name}`}
                        label={label}
                        isEmpty={!rest}
                        hasErrors={!!error}
                        error={error}
                    >
                        <InputGroup className="bg-paper">
                            <InputGroupAddon align="inline-start">
                                <InputGroupText>{HTTPS}</InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                                ref={ref}
                                id={`field_${name}`}
                                value={rest}
                                inputMode="url"
                                autoComplete="off"
                                aria-invalid={!!error}
                                onBlur={onBlur}
                                onChange={(event) => {
                                    const typed = event.target.value.replace(SCHEME, '').trim();
                                    onChange(typed ? HTTPS + typed : '');
                                }}
                            />
                        </InputGroup>
                    </InputWrapper>
                );
            }}
        />
    );
}
